import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { CheckIcon, CloseIcon } from './icons';

interface EditableNoteProps {
  value: string | null;
  placeholder?: string;
  className?: string;
  onSave: (newValue: string | null) => Promise<void>;
  singleLine?: boolean;
  onValidate?: (value: string) => Promise<boolean>;
  validateDebounceMs?: number;
}

export function EditableNote({
  value,
  placeholder = 'Add a note...',
  className = 'vertex-note',
  onSave,
  singleLine = false,
  onValidate,
  validateDebounceMs = 400,
}: EditableNoteProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [draftValue, setDraftValue] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Validation state
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const validateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Determine what to display
  const hasDraft = draftValue !== null;
  const displayValue = hasDraft ? draftValue : value;
  const showDraftIndicator = hasDraft && draftValue !== value;

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Move cursor to end
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [isEditing]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (validateDebounceRef.current) clearTimeout(validateDebounceRef.current);
    };
  }, []);

  function startEditing() {
    setIsEditing(true);
    setEditValue(displayValue || '');
    setIsValid(null);
    setIsValidating(false);
    if (validateDebounceRef.current) {
      clearTimeout(validateDebounceRef.current);
      validateDebounceRef.current = null;
    }
  }

  function cancelEditing() {
    // Save as draft without calling backend
    const trimmed = editValue.trim();
    if (trimmed !== (value || '')) {
      setDraftValue(trimmed || null);
    }
    setIsEditing(false);
    setEditValue('');
    setIsValid(null);
    setIsValidating(false);
    if (validateDebounceRef.current) {
      clearTimeout(validateDebounceRef.current);
      validateDebounceRef.current = null;
    }
  }

  function runValidation(val: string) {
    if (!onValidate) return;

    // Clear any pending validation
    if (validateDebounceRef.current) {
      clearTimeout(validateDebounceRef.current);
      validateDebounceRef.current = null;
    }

    const trimmed = val.trim();
    // If empty, no validation needed
    if (!trimmed) {
      setIsValid(null);
      setIsValidating(false);
      return;
    }

    // If same as current value (case-insensitive), it's valid (no change)
    if (trimmed.toLowerCase() === (value || '').toLowerCase()) {
      setIsValid(true);
      setIsValidating(false);
      return;
    }

    // Need async validation - show loading state
    setIsValid(null);
    setIsValidating(true);

    validateDebounceRef.current = setTimeout(() => {
      onValidate(trimmed)
        .then((result) => {
          setIsValidating(false);
          setIsValid(result ? true : false);
        })
        .catch(() => {
          setIsValidating(false);
          setIsValid(null);
        });
    }, validateDebounceMs);
  }

  function handleInput(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    setEditValue(val);
    runValidation(val);
  }

  async function save() {
    if (isSaving) return;
    // Block save if validation failed
    if (onValidate && isValid === false) return;

    try {
      setIsSaving(true);
      const newValue = editValue.trim() || null;
      await onSave(newValue);

      // Clear draft on successful save
      setDraftValue(null);
      setIsEditing(false);
      setEditValue('');
      setIsValid(null);
    } catch (err) {
      alert(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (singleLine && e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (!singleLine && e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditing();
    }
  }

  function handleBlur(e: FocusEvent) {
    const nextFocused = e.relatedTarget as HTMLElement | null;
    if (nextFocused && containerRef.current?.contains(nextFocused)) {
      return;
    }
    setTimeout(() => {
      if (isEditing) {
        cancelEditing();
      }
    }, 200);
  }

  const editTitle = singleLine ? 'Enter to save, Escape to cancel' : 'Ctrl+Enter to save, Escape to cancel';
  const saveDisabled = isSaving || isValidating || (onValidate !== undefined && isValid === false);

  return (
    <div
      ref={containerRef}
      class={isEditing ? `${className} editing` : `${className}${showDraftIndicator ? ' has-draft' : ''}`}
      onClick={() => !isEditing && startEditing()}
      title={isEditing ? editTitle : 'Click to edit'}
    >
      {isEditing ? (
        <div class="editable-note-input-row">
          {singleLine ? (
            <input
              ref={inputRef as preact.RefObject<HTMLInputElement>}
              type="text"
              class="vertex-note-input"
              value={editValue}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={isSaving}
            />
          ) : (
            <textarea
              ref={inputRef as preact.RefObject<HTMLTextAreaElement>}
              class="vertex-note-input"
              value={editValue}
              onInput={handleInput}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              placeholder={placeholder}
              rows={3}
              disabled={isSaving}
            />
          )}
          {onValidate && (
            <span class="validation-status">
              {isValidating && <span class="validation-checking">…</span>}
              {!isValidating && isValid === true && <span class="validation-valid">available</span>}
              {!isValidating && isValid === false && <span class="validation-invalid">unavailable</span>}
            </span>
          )}
          <button
            type="button"
            class="editable-note-action editable-note-action-save"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => { e.stopPropagation(); save(); }}
            disabled={saveDisabled}
            title={editTitle}
            aria-label="Save"
          >
            <CheckIcon size={14} strokeWidth={2} />
          </button>
          <button
            type="button"
            class="editable-note-action editable-note-action-cancel"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => { e.stopPropagation(); cancelEditing(); }}
            disabled={isSaving}
            title="Cancel (Escape)"
            aria-label="Cancel"
          >
            <CloseIcon size={14} strokeWidth={2} />
          </button>
        </div>
      ) : (
        <>
          {displayValue || <span class="placeholder">{placeholder}</span>}
          {showDraftIndicator && <span class="draft-indicator">Draft</span>}
        </>
      )}
    </div>
  );
}
